import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import lodashFind from 'lodash/find';
import lodashIsEmpty from 'lodash/isEmpty';

import constants from '../../../Config/constants';
import useMealPlan from '../../../Hooks/useMealPlan';
import { updateCheckoutDetails } from '../../../RTK/checkout';
import {
  checkoutStoreInfoSelector,
  selectCheckoutData,
  selectCheckoutItems,
  selectFormData,
} from '../../../RTK/checkout/selectors';
import { checkout } from '../../../RTK/defaultValues';
import { setMealPlanCheckoutData } from '../../../RTK/mealPlan';
import Service from '../../../Service';
import storeApi from '../../../Service/api/store';

const EMPTY_OBJ = {}; // outside lifecycle to prevent new reference on rerender
const useCheckoutHandler = () => {
  const dispatch = useDispatch();
  const { isMealPlan } = useMealPlan();
  const checkoutInfo = useSelector(checkoutStoreInfoSelector);
  const itemSelector = useMemo(
    () => selectCheckoutItems(checkoutInfo?.store_id, isMealPlan),
    [checkoutInfo?.store_id, isMealPlan],
  );
  const cdSelector = useMemo(
    () => selectCheckoutData(checkoutInfo?.store_id, isMealPlan),
    [checkoutInfo?.store_id, isMealPlan],
  );
  const baseFormSelector = useCallback(
    (key) => selectFormData(checkoutInfo?.store_id, isMealPlan, key),
    [checkoutInfo?.store_id, isMealPlan],
  );
  const checkoutItems = useSelector(itemSelector);
  const checkoutData = useSelector(cdSelector);
  // form values selector
  const otSelector = useMemo(
    () => baseFormSelector(checkout.keys.ORDER_TYPE),
    [baseFormSelector],
  );
  const daSelector = useMemo(
    () => baseFormSelector(checkout.keys.DELIVERY_ADDRESS),
    [baseFormSelector],
  );
  const dsSelector = useMemo(
    () => baseFormSelector(checkout.keys.DELIVERY_SCHEDULE),
    [baseFormSelector],
  );
  const dspdSelector = useMemo(
    () => baseFormSelector(checkout.keys.DELIVERY_SPEED),
    [baseFormSelector],
  );
  const pmSelector = useMemo(
    () => baseFormSelector(checkout.keys.PAYMENT_METHOD),
    [baseFormSelector],
  );
  const poSelector = useMemo(
    () => baseFormSelector(checkout.keys.PAYMENT_OPTIONS),
    [baseFormSelector],
  );
  const tpSelector = useMemo(
    () => baseFormSelector(checkout.keys.THIRD_PARTY_PICKUP),
    [baseFormSelector],
  );
  const userSelector = useMemo(
    () => baseFormSelector(checkout.keys.USER_DETAILS),
    [baseFormSelector],
  );
  const viSelector = useMemo(
    () => baseFormSelector(checkout.keys.VEHICLE_INFORMATION),
    [baseFormSelector],
  );
  const pcSelector = useMemo(
    () => baseFormSelector(checkout.keys.PROMO_CODE),
    [baseFormSelector],
  );
  const voSelector = useMemo(
    () =>
      selectFormData(
        checkoutInfo?.store_id,
        isMealPlan,
        checkout.keys.VEHICLE_OPTIONS,
      ),
    [checkoutInfo?.store_id, isMealPlan],
  );
  // form values
  const formDeliveryAddress = useSelector(daSelector);
  const formDeliverySchedule = useSelector(dsSelector);
  const formDeliverySpeed = useSelector(dspdSelector);
  const formOrderType = useSelector(otSelector);
  const formPaymentMethod = useSelector(pmSelector);
  const formPaymentOptions = useSelector(poSelector);
  const formPromoCode = useSelector(pcSelector);
  const formThirdPartyPickDetails = useSelector(tpSelector);
  const formUserDetails = useSelector(userSelector);
  const formVehicleInformation = useSelector(viSelector);
  const formVehicleOptions = useSelector(voSelector);

  const isDelivery = formOrderType?.value === constants.ORDER_TYPES.DELIVERY;
  const isPickup = formOrderType?.value === constants.ORDER_TYPES.PICKUP;
  const isCurbside = formOrderType?.value === constants.ORDER_TYPES.CURBSIDE;

  // #region Additional Charge
  const addChargeByOrderType = lodashFind(
    checkoutInfo.additional_charges,
    (e) => e.name.indexOf(`${formOrderType?.value}_`) === 0, // e.g: pickup_service_charge, curbside_pickup_service_charge (pickup_) will return the pickup not curbside since the indexOf is returned 0
  );
  const hasAdditionalCharge = !lodashIsEmpty(addChargeByOrderType);
  const { data: addChargeRes, isFetching: isAddChargeLoading } = useQuery({
    enabled:
      !isMealPlan && // not meal plan
      Boolean(checkoutInfo.store_id) && // has store id
      Boolean(formOrderType?.value) && // has order type
      hasAdditionalCharge, // has additional charges
    queryFn: () =>
      storeApi.getAdditionalCharge({
        store_id: checkoutInfo.store_id,
        order_type: formOrderType?.value,
      }),
    queryKey: [
      'additional-charge',
      checkoutInfo.store_id,
      formOrderType?.value,
      hasAdditionalCharge,
    ],
    refetchOnWindowFocus: false,
  });
  const addChargeData = addChargeRes?.ok
    ? addChargeRes?.data || EMPTY_OBJ
    : EMPTY_OBJ;
  const addChargeError = !lodashIsEmpty(addChargeData?.message)
    ? Service.handleErrorMessage(addChargeData?.message?.[0])
    : '';
  const additionalCharge = useMemo(
    () =>
      isMealPlan
        ? undefined
        : {
            breakdownFactor: addChargeByOrderType,
            data: addChargeData,
            enabled: hasAdditionalCharge,
            error: addChargeError,
            loading: isAddChargeLoading,
            total: hasAdditionalCharge
              ? addChargeData?.total_additional_charges
              : 0,
          },
    [
      isMealPlan,
      addChargeByOrderType,
      addChargeData,
      addChargeError,
      hasAdditionalCharge,
      isAddChargeLoading,
    ],
  );
  // #regionend

  const _onUpdateCheckoutDetails = useCallback(
    (key: string, value: any) => {
      if (isMealPlan) {
        dispatch(
          setMealPlanCheckoutData({
            key: key,
            data: value,
          }),
        );
      } else {
        dispatch(
          updateCheckoutDetails({
            store_id: checkoutInfo.store_id,
            keyToUpdate: key,
            keyValue: value,
          }),
        );
      }
    },
    [checkoutInfo.store_id, isMealPlan, dispatch],
  );

  return {
    additionalCharge,
    checkoutData,
    checkoutInfo,
    checkoutItems,
    formDeliveryAddress,
    formDeliverySchedule,
    formDeliverySpeed,
    formPaymentMethod,
    formPaymentOptions,
    formPromoCode,
    formThirdPartyPickDetails,
    formUserDetails,
    formVehicleInformation,
    formVehicleOptions,
    isCurbside,
    isDelivery,
    isPickup,
    orderType: formOrderType,
    onUpdateCheckoutDetails: _onUpdateCheckoutDetails,
  };
};

export default useCheckoutHandler;
